import _extends from "@babel/runtime/helpers/extends";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
import _typeof from "@babel/runtime/helpers/typeof";
var _excluded = ["alignContent", "alignItems", "alignSelf", "backgroundColor", "borderRadius", "border", "borderColor", "borderTop", "borderBottom", "borderLeft", "borderRight", "children", "className", "component", "display", "flexDirection", "flexWrap", "gridGap", "height", "justifyContent", "margin", "marginX", "marginY", "marginTop", "marginBottom", "marginLeft", "marginRight", "maxWidth", "opacity", "padding", "paddingX", "paddingY", "paddingTop", "paddingBottom", "paddingLeft", "paddingRight", "position", "shadow", "style", "userSelect", "width"];
import "core-js/modules/es6.array.filter.js";
import React from 'react';
import classnames from 'classnames';
import baseStyles, { mergeStyles } from '@nerdwallet/base-styles/react';
import { useGridGap } from '../hooks/useGridGap';

/**
 * Converts a prop value (either a literal, like `4`, or a responsive value,
 * like `{ mobile: 2, desktop: 4}`) to a value that can be passed to
 * `classes(...)` to resolve to classnames.
 *
 * The conversion is based on the convention that `base-styles` tokens are
 * generally in the form `NAME-VALUE`, e.g. `margin-all-3`, where VALUE is
 * the prop value. For desktop values, `NAME-VALUE--wide` is used instead.
 *
 * For tokens that don't follow this pattern, like `flex-wrap` (which has the
 * tokens `flex-wrap` and `flex-wrap-nowrap`), you can provide a custom
 * `resolvePropValue` function.
 */
function resolveResponsiveBaseStylesProp(props, name) {
  var resolvePropValue = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : function (value) {
    return "".concat(name, "-").concat(value);
  };

  if (props == null) {
    return undefined;
  }

  if (typeof props === 'string' || typeof props === 'number') {
    // Simple case - only need a single value
    return resolvePropValue(props);
  }

  if (_typeof(props) === 'object') {
    var mobile = props.mobile,
        desktop = props.desktop;
    return [mobile != null && resolvePropValue(mobile), desktop != null && "".concat(resolvePropValue(desktop), "--wide")].filter(Boolean);
  }

  throw new Error("Invalid responsive props: ".concat(JSON.stringify(props)));
}

export var BoxImpl = /*#__PURE__*/React.forwardRef(function (_ref, ref) {
  var _ref2, _ref3, _ref4, _ref5, _ref6, _ref7, _ref8, _ref9;

  var alignContent = _ref.alignContent,
      alignItems = _ref.alignItems,
      alignSelf = _ref.alignSelf,
      backgroundColor = _ref.backgroundColor,
      borderRadius = _ref.borderRadius,
      border = _ref.border,
      borderColor = _ref.borderColor,
      borderTop = _ref.borderTop,
      borderBottom = _ref.borderBottom,
      borderLeft = _ref.borderLeft,
      borderRight = _ref.borderRight,
      children = _ref.children,
      className = _ref.className,
      Component = _ref.component,
      display = _ref.display,
      flexDirection = _ref.flexDirection,
      flexWrap = _ref.flexWrap,
      gridGap = _ref.gridGap,
      height = _ref.height,
      justifyContent = _ref.justifyContent,
      margin = _ref.margin,
      marginX = _ref.marginX,
      marginY = _ref.marginY,
      marginTop = _ref.marginTop,
      marginBottom = _ref.marginBottom,
      marginLeft = _ref.marginLeft,
      marginRight = _ref.marginRight,
      maxWidth = _ref.maxWidth,
      opacity = _ref.opacity,
      padding = _ref.padding,
      paddingX = _ref.paddingX,
      paddingY = _ref.paddingY,
      paddingTop = _ref.paddingTop,
      paddingBottom = _ref.paddingBottom,
      paddingLeft = _ref.paddingLeft,
      paddingRight = _ref.paddingRight,
      position = _ref.position,
      shadow = _ref.shadow,
      style = _ref.style,
      userSelect = _ref.userSelect,
      width = _ref.width,
      restProps = _objectWithoutProperties(_ref, _excluded);

  var resolvedMarginTop = (_ref2 = marginTop !== null && marginTop !== void 0 ? marginTop : marginY) !== null && _ref2 !== void 0 ? _ref2 : margin;
  var resolvedMarginBottom = (_ref3 = marginBottom !== null && marginBottom !== void 0 ? marginBottom : marginY) !== null && _ref3 !== void 0 ? _ref3 : margin;
  var resolvedMarginLeft = (_ref4 = marginLeft !== null && marginLeft !== void 0 ? marginLeft : marginX) !== null && _ref4 !== void 0 ? _ref4 : margin;
  var resolvedMarginRight = (_ref5 = marginRight !== null && marginRight !== void 0 ? marginRight : marginX) !== null && _ref5 !== void 0 ? _ref5 : margin;
  var resolvedPaddingTop = (_ref6 = paddingTop !== null && paddingTop !== void 0 ? paddingTop : paddingY) !== null && _ref6 !== void 0 ? _ref6 : padding;
  var resolvedPaddingBottom = (_ref7 = paddingBottom !== null && paddingBottom !== void 0 ? paddingBottom : paddingY) !== null && _ref7 !== void 0 ? _ref7 : padding;
  var resolvedPaddingLeft = (_ref8 = paddingLeft !== null && paddingLeft !== void 0 ? paddingLeft : paddingX) !== null && _ref8 !== void 0 ? _ref8 : padding;
  var resolvedPaddingRight = (_ref9 = paddingRight !== null && paddingRight !== void 0 ? paddingRight : paddingX) !== null && _ref9 !== void 0 ? _ref9 : padding;
  var resolvedBorderTop = borderTop !== null && borderTop !== void 0 ? borderTop : border;
  var resolvedBorderBottom = borderBottom !== null && borderBottom !== void 0 ? borderBottom : border;
  var resolvedBorderLeft = borderLeft !== null && borderLeft !== void 0 ? borderLeft : border;
  var resolvedBorderRight = borderRight !== null && borderRight !== void 0 ? borderRight : border;
  var gridGapClassName = useGridGap(gridGap);
  return /*#__PURE__*/React.createElement(Component, _extends({}, restProps, mergeStyles(baseStyles(resolveResponsiveBaseStylesProp(alignContent, 'align-content'), resolveResponsiveBaseStylesProp(alignItems, 'align-items'), resolveResponsiveBaseStylesProp(alignSelf, 'align-self'), resolveResponsiveBaseStylesProp(backgroundColor, 'background'), resolveResponsiveBaseStylesProp(borderRadius, 'border-radius'), resolveResponsiveBaseStylesProp(borderColor, 'border'), resolveResponsiveBaseStylesProp(resolvedBorderTop, 'border-top'), resolveResponsiveBaseStylesProp(resolvedBorderBottom, 'border-bottom'), resolveResponsiveBaseStylesProp(resolvedBorderLeft, 'border-left'), resolveResponsiveBaseStylesProp(resolvedBorderRight, 'border-right'), resolveResponsiveBaseStylesProp(display, 'display'), resolveResponsiveBaseStylesProp(flexDirection, 'flex-direction'), resolveResponsiveBaseStylesProp(flexWrap, 'flex-wrap', function (token) {
    if (token === 'wrap') {
      return 'flex-wrap';
    }

    return 'flex-nowrap';
  }), resolveResponsiveBaseStylesProp(height, 'height'), resolveResponsiveBaseStylesProp(justifyContent, 'justify-content'), resolveResponsiveBaseStylesProp(resolvedMarginTop, 'margin-top'), resolveResponsiveBaseStylesProp(resolvedMarginBottom, 'margin-bottom'), resolveResponsiveBaseStylesProp(resolvedMarginLeft, 'margin-left'), resolveResponsiveBaseStylesProp(resolvedMarginRight, 'margin-right'), resolveResponsiveBaseStylesProp(maxWidth, 'max-width'), resolveResponsiveBaseStylesProp(opacity, 'opacity'), resolveResponsiveBaseStylesProp(resolvedPaddingTop, 'padding-top'), resolveResponsiveBaseStylesProp(resolvedPaddingBottom, 'padding-bottom'), resolveResponsiveBaseStylesProp(resolvedPaddingLeft, 'padding-left'), resolveResponsiveBaseStylesProp(resolvedPaddingRight, 'padding-right'), resolveResponsiveBaseStylesProp(position, 'position'), resolveResponsiveBaseStylesProp(shadow, 'shadow'), resolveResponsiveBaseStylesProp(userSelect, 'user-select'), resolveResponsiveBaseStylesProp(width, 'width')), {
    className: classnames(gridGapClassName, className),
    style: style
  }), {
    ref: ref
  }), children);
});